import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal, ModalHeader, ModalBody, Row, Col, Spinner } from 'reactstrap';
import CommonButton from '../common/components/CommonButton';
import { CommonButtonVariants } from '../constants/CommonButtonVariants';
import SettingsSectionHeader from '../settings/SettingsSectionHeader';
import '../assets/css/componentStyles/PublishOfferModal.css';

function PublishOfferModal({
  isOpen,
  toggle,
  onConfirm,
  isPublishing,
  cancelButtonText,
  className,
  size,
  showSpinner,
  onCancel,
  disabled,
  publishLabel,
  isScheduledAuction,
}) {
  const handleConfirm = useCallback(() => {
    if (!disabled && !isPublishing && onConfirm) {
      toggle();
      onConfirm();
    }
  }, [onConfirm, toggle, disabled, isPublishing]);

  const handleCancel = useCallback(() => {
    if (!disabled && !isPublishing) {
      if (onCancel) {
        onCancel();
      }
      toggle();
    }
  }, [toggle, onCancel, disabled, isPublishing]);

  const modalContent = useMemo(
    () => ({
      title: isScheduledAuction ? 'Schedule Offer' : 'Publish Offer',
      message: isScheduledAuction
        ? 'Please confirm that you want to schedule the offer.'
        : 'Please confirm that you want to publish the offer.',
      confirmText: publishLabel || 'PUBLISH OFFER',
      cancelText: cancelButtonText || 'CANCEL',
    }),
    [isScheduledAuction, publishLabel, cancelButtonText],
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      size={size}
      backdropClassName="publish-offer-modal-backdrop"
      className={classNames('publish-offer-modal', className)}
      aria-labelledby="publish-offer-modal-title"
      aria-describedby="publish-offer-modal-description"
      role="dialog"
      aria-modal="true"
    >
      <ModalHeader toggle={handleCancel} tag="div">
        <SettingsSectionHeader
          header={modalContent.title}
          variant="noBorder"
          id="publish-offer-modal-title"
        />
      </ModalHeader>
      <ModalBody>
        <div className="confirmation-text mt-2 mb-5" id="publish-offer-modal-description">
          {modalContent.message}
        </div>
        {isPublishing && showSpinner && (
          <div className="d-flex justify-content-center my-3">
            <Spinner color="primary" size="sm" />
            <span className="ms-2">Publishing...</span>
          </div>
        )}
        <Row className="mt-4 mb-3">
          <Col md={6}>
            <CommonButton
              label={
                isPublishing && showSpinner ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Publishing...
                  </>
                ) : (
                  modalContent.confirmText
                )
              }
              variant={CommonButtonVariants.PRIMARY_RED}
              handleClick={handleConfirm}
              disabled={disabled || isPublishing}
              className="w-100"
              aria-describedby="publish-offer-modal-description"
            />
          </Col>
          <Col md={6}>
            <CommonButton
              label={modalContent.cancelText}
              variant={CommonButtonVariants.OUTLINED_RED}
              handleClick={handleCancel}
              disabled={disabled || isPublishing}
              className="w-100"
            />
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
}

PublishOfferModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isPublishing: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg', 'xl']),
  showSpinner: PropTypes.bool,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
};

PublishOfferModal.defaultProps = {
  isPublishing: false,
  cancelButtonText: null,
  className: '',
  size: 'lg',
  showSpinner: true,
  onCancel: null,
  disabled: false,
};

export default PublishOfferModal;
